import Navigation from "../../common/components/Navigation/Navigation";
import DownloadClientButton from "../../features/downloadClient/components/DownloadClientButton";
import type { ReactElement } from "react";
import React from "react";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import {
  getLatestWixarClientImacUri,
  getLatestWixarClientVersion,
  getLatestWixarClientWindowsUri,
  getWixarClientUris,
} from "../../features/downloadClient/state/downloadClientSlice";
import { IsLoading } from "../../common/components/AppState/IsLoading";
import { useTranslation } from "react-i18next";
import { languageConfig } from "src/common/util/langConfig";
import { getCurrentUser } from "src/features/profile/state/profileSlice";
import type { ILanguage } from "src/model/model";
export const DOWNLOAD_WIXAR_CLIENT_COMPONENT_ID = "downloadWixarClientComponentId";

export default function DownloadClient(): ReactElement {
  const dispatch = useAppDispatch();

  const latestWixarClientWindows = useAppSelector(getLatestWixarClientWindowsUri);
  const latestWixarClientImac = useAppSelector(getLatestWixarClientImacUri);
  const latestWixarClientVersion = useAppSelector(getLatestWixarClientVersion);

  React.useEffect(() => {
    void dispatch(getWixarClientUris({ componentId: DOWNLOAD_WIXAR_CLIENT_COMPONENT_ID }));
  }, []);
  interface PlatformComponentProps {
    platform: string;
    version: string;
    size: string;
    lastestVersionHref?: string;
    buttonActivated?: boolean;
    platformIcon?: string;
  }
  interface TableForPlatformComponentProps {
    children: React.ReactNode;
  }
  const TableForPlatformComponent = ({ children }: TableForPlatformComponentProps) => {
    const { t } = useTranslation();

    return (
      <div className="px-4 sm:px-8 md:px-12">
        <h1 className="text-xl font-bold text-gray-800 mb-2">{t("pages.downloadClient.header")}</h1>
        <p className="text-gray-600 text-sm mb-8">{t("pages.downloadClient.headerBis")}</p>

        {/* Enum Section */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">
            {t("pages.downloadClient.enumTitle")}
          </h2>
          <ul className="list-disc pl-5 text-gray-600 text-sm space-y-1">
            <li>{t("pages.downloadClient.enum1")}</li>
            <li>{t("pages.downloadClient.enum2")}</li>
            <li>{t("pages.downloadClient.enum3")}</li>
            <li>{t("pages.downloadClient.enum4")}</li>
          </ul>
        </div>

        {/* Actual Table */}
        <table className="w-full text-sm text-left text-gray-800 border-collapse">
          <thead className="border-b font-medium">
            <tr>
              <th className="py-2 px-4">{t("general.platforms")}</th>
              <th className="py-2 px-4">{t("general.lastVersion")}</th>
              <th className="py-2 px-4">{t("general.weightStudio")}</th>
              <th className="py-2 px-4 text-right"></th>
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    );
  };

  const PlatformComponent = ({
    platform,
    version,
    size,
    lastestVersionHref = "",
    buttonActivated = true,
    platformIcon = "windows10",
  }: PlatformComponentProps) => {
    const { t } = useTranslation();
    const currentUser = useAppSelector(getCurrentUser);
    const currentLocale = languageConfig((currentUser?.preferred_language as ILanguage)?.name);

    return (
      <tr className="border-b last:border-0">
        <td className="py-3 px-4 whitespace-nowrap">{platform}</td>
        <td className="py-3 px-4 whitespace-nowrap">V {version}</td>
        <td className="py-3 px-4 whitespace-nowrap">
          {size} {currentLocale === "fr" ? "Mo" : "Mb"}
        </td>
        <td className="py-3 px-4 text-right whitespace-nowrap">
          {buttonActivated ? (
            <DownloadClientButton
              platform={platformIcon}
              styled="w-44 h-9 rounded-md bg-green-500 hover:shadow-lg hover:scale-105"
              href={lastestVersionHref}
              textInfo={t("pages.downloadClient.windows10Explanation")}
              textIcon={t("general.download")}
            />
          ) : (
            <button className="w-40 text-sm">
              <span className="underline underline-offset-4 text-purple-600">
                {t("studioDownload.linkLab")}
              </span>
            </button>
          )}
        </td>
      </tr>
    );
  };

  return (
    <>
      <Navigation title={"Wixar Hub"} />
      <IsLoading componentId={DOWNLOAD_WIXAR_CLIENT_COMPONENT_ID} showSuccess={false}>
        <div className=" mt-10 pl-[9rem] overflow-auto w-full h-full personalize-scroll-visible ">
          <div className="mt-2 mr-5">
            <TableForPlatformComponent>
              <PlatformComponent
                platformIcon="Windows10"
                platform="Wixar Hub Windows 10/11"
                version={latestWixarClientVersion}
                size="83"
                lastestVersionHref={latestWixarClientWindows}
              />

              <PlatformComponent
                platformIcon="MacOS"
                platform="Wixar Hub MacOS"
                version={latestWixarClientVersion}
                size="110"
                lastestVersionHref={latestWixarClientImac}
              />
            </TableForPlatformComponent>
          </div>
        </div>
      </IsLoading>
      {
        /* for development regarding ErrorBoundary:
        uncomment next line to purposely crash this view */
        // Promise.reject("oopsie")
      }
    </>
  );
}
